import { memo } from 'react';
import { Package, CreditCard, Truck, Calendar, Layers } from 'lucide-react';
import { VentaActividad, ProductoActividad, Miembro } from '../../lib/supabase';
import { ProduccionConMetricas } from '../../services/produccionService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { getNombreCliente, getNombreReferido, tieneReferido } from '../../utils/clienteHelpers';

interface PedidoCardProps {
  readonly pedido: VentaActividad;
  readonly productos: ProductoActividad[];
  readonly producciones: ProduccionConMetricas[];
  readonly miembros: Miembro[];
  readonly onClickCard: () => void;
  readonly onRegistrarPago: () => void;
  readonly onRegistrarEntrega: () => void;
  readonly readOnly?: boolean;
  readonly showProducto?: boolean;
}

export const PedidoCard = memo<PedidoCardProps>(({
  pedido,
  productos,
  producciones,
  miembros,
  onClickCard,
  onRegistrarPago,
  onRegistrarEntrega,
  readOnly = false,
  showProducto = true,
}) => {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200">
      {/* Área clickeable para abrir detalle */}
      <div 
        onClick={onClickCard}
        className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
      >
        {/* Nombre y Monto */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex flex-col justify-center flex-1 min-w-0">
            <h3 className="font-bold text-slate-900 text-lg leading-tight">
              {getNombreCliente(pedido, miembros)}
            </h3>
            {tieneReferido(pedido) && (
              <p className="text-xs text-slate-500 leading-tight mt-0.5">
                ref. {getNombreReferido(pedido, miembros)}
              </p>
            )}
          </div>
          <p className="text-xl sm:text-2xl font-bold text-blue-600 whitespace-nowrap">
            {formatCurrency(pedido.monto_total)}
          </p>
        </div>
        
        {/* Línea divisoria */}
        <div className="border-t border-slate-100 my-3" />

        {/* Producto y Cantidad */}
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-4 h-4 text-purple-500 flex-shrink-0" />
          <span className="text-sm text-slate-600">
            {showProducto && (
              <span className="font-semibold text-purple-700">
                {productos.find(p => p.id === pedido.producto_id)?.nombre_producto || 'Producto'}
                {' '}
              </span>
            )}
            <span className="text-slate-500">{showProducto ? `x ${pedido.cantidad} unid.` : `${pedido.cantidad} unid.`}</span>
          </span>
        </div>

        {/* Lote asociado */}
        {pedido.produccion_id && (
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-purple-500 flex-shrink-0" />
            <span className="text-sm font-semibold text-purple-700">
              {producciones.find(p => p.id === pedido.produccion_id)?.nombre || 'Lote'}
            </span>
          </div>
        )}

        {/* Estado de Pago */}
        <div className="flex items-center gap-2 mb-2">
          <CreditCard className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          <span className="text-sm text-slate-600">
            <span className="font-medium text-emerald-700">{formatCurrency(pedido.monto_pagado)}</span>
            <span className="text-slate-500"> de {formatCurrency(pedido.monto_total)}</span>
          </span>
        </div>

        {/* Estado de Entrega */}
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <span className="text-sm text-slate-600">
            <span className="font-medium text-blue-700">{pedido.cantidad_entregada}</span>
            <span className="text-slate-500"> de {pedido.cantidad} unidades</span>
          </span>
        </div>

        {/* Fecha y Hora de Entrega (si existe) */}
        {(pedido.fecha_entrega || pedido.rango_horario_retiro_despacho) && (
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <span className="text-sm text-slate-600">
              {pedido.fecha_entrega && <span className="font-medium text-slate-700">{formatDate(pedido.fecha_entrega)}</span>}
              {pedido.fecha_entrega && pedido.rango_horario_retiro_despacho && <span className="text-slate-500"> • </span>}
              {pedido.rango_horario_retiro_despacho && <span className="font-medium text-slate-700">{pedido.rango_horario_retiro_despacho}</span>}
            </span>
          </div>
        )}

        {/* Forma de Entrega */}
        <div className="flex items-center gap-2 mb-3">
          <Truck className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <span className="text-sm text-slate-600 truncate">
            <span className="font-medium text-slate-700">
              {pedido.forma_entrega === 'despacho' ? pedido.direccion_despacho || 'Sin dirección' : 'Retiro en sede'}
            </span>
          </span>
        </div>

        {/* Indicador de "Toca para ver detalle" */}
        <div className="text-center pt-2 border-t border-slate-200">
          <span className="text-xs text-slate-400 font-medium">Toca para ver detalle completo</span>
        </div>
      </div>
      
      {/* Botones de acción rápida (no clickeables para el detalle) */}
      {!readOnly && (
        <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 border-t border-slate-200">
          {pedido.estado_pago !== 'pagado' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRegistrarPago();
              }}
              className="px-3 py-2 text-xs bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors flex items-center justify-center gap-1.5 font-medium"
            >
              <CreditCard className="w-4 h-4" />
              Registrar Pago
            </button>
          )}
          {pedido.estado_entrega !== 'entregado' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRegistrarEntrega();
              }}
              className="px-3 py-2 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center justify-center gap-1.5 font-medium"
            >
              <Truck className="w-4 h-4" />
              Registrar Entrega
            </button>
          )}
        </div>
      )}
    </div>
  );
});
